import {
  getRefCountedManager,
  IModuleManager
} from "redux-dynamic-modules-core";
import createSagaMiddleware from "redux-saga";

import {
  SagaContext,
  SagaExtension,
  SagaManager,
  SagaModule
} from "./contracts";
import getSagaManager from "./manager";
import sagaEquals from "./sagaEquals";
import { DEFERRED } from "../../constants";

/**
 * Get an extension that integrates saga with the store
 * sagaContext param is the context to provide to the saga
 */
export default function getSagaExtension(
  sagaContext: SagaContext = {},
  onError?: (error: Error) => void
): SagaExtension {
  // Setup the saga middleware
  const sagaMiddleware = createSagaMiddleware<any>({
    context: sagaContext,
    onError
  });

  const sagaManager: SagaManager = getRefCountedManager(
    getSagaManager(sagaMiddleware),
    sagaEquals
  );

  const createExposedPromise = () => {
    const deferred = {};

    const promise = new Promise((resolve, reject) => {
      (deferred as any).resolve = resolve;
      (deferred as any).reject = reject;
    });

    return [promise, deferred];
  };

  const customMiddleware = () => next => action => {
    // Invalid action exclusion
    if (action) {
      if (!action.meta || !action.meta[DEFERRED]) {
        return next(action);
      }
      const [promise, deferred] = createExposedPromise();
      next({ ...action, [DEFERRED]: deferred });
      return promise;
    }
  };

  return {
    sagaTasks: sagaManager.sagaTasks,

    middleware: [customMiddleware, sagaMiddleware],

    onModuleManagerCreated: (moduleManager: IModuleManager) => {
      if (sagaContext) {
        // eslint-disable-next-line no-param-reassign
        sagaContext.moduleManager = moduleManager;
      }
    },

    onModuleAdded: (module: SagaModule) => {
      if (module.sagas) {
        sagaManager.add(module.sagas);
      }
    },

    onModuleRemoved: (module: SagaModule) => {
      if (module.sagas) {
        sagaManager.remove(module.sagas);
      }
    },

    dispose: () => {
      sagaManager.dispose();
    }
  };
}
